import React from 'react';
import { Box, Flex, Text, UnstyledButton, ScrollArea, Tooltip } from '@mantine/core';
import type { ActiveFilter } from '../../types';
import SelectedFilterValues from './SelectedFilterValues';
import { capitalizeFirstLetter } from '../../../../../utils/capitalizeFirstLetter';
import { StackFront } from '../../../../../icons/x-symbol-svgrepo-com';
import { areFiltersEquivalent } from '../../../../evidences/utils/filterComparison';
import { MAIN_DARK_PRIMARY } from '../../../../../constants/colors';
import {
  formatFilterValues,
  getFilterTypeLabel,
  formatChangeLogFilterLabel,
  isChangeLogFilter
} from '../../../../../utils/filterUtils'

const pillSx = {
  backgroundColor: '#f5f6f8',
  color: '#374151',
  borderRadius: 6,
  padding: '6px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  border: '1px solid #e2e4e8',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
} as const;
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";

export interface SavedFiltersProps {
  /** Currently applied filters to show as rows */
  filters: ActiveFilter[];
  /** View filters to compare against (to show "Applied by view" icon) */
  viewFilters?: Array<{ name: string; selected?: any }>;
  /** Filters config to look up display names (e.g. for owner filter) */
  filtersConfig?: any;
  /** Remove one filter by name */
  onRemoveFilter: (filterName: string) => void;
  /** Optional: focus this filter in the modal (e.g. for editing) */
  onSelectFilter?: (filter: ActiveFilter) => void;
}

const SAVED_FILTERS_HEIGHT = '13vh';

const SavedFilters: React.FC<SavedFiltersProps> = ({
  filters,
  viewFilters,
  filtersConfig,
  onRemoveFilter,
  onSelectFilter,
}) => {
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id || null;



  // Helper to check if a filter is from the view
  const isViewFilter = (filter: ActiveFilter): boolean => {
    if (!viewFilters || !Array.isArray(viewFilters) || viewFilters.length === 0) {
      return false;
    }
    return viewFilters.some((viewFilter) => areFiltersEquivalent(filter, viewFilter));
  };

  // Filter out filters with undefined selected property, empty array, or state filter.
  // Keep filters that have a savedKey even if selected is empty (dynamic date filters).
  const validFilters = (filters?.filter((filter) => {
    if (filter.name === 'state' && filter.filterObject === 'evidence') return false;
    const hasSavedKey = !!(filter as any).savedKey;
    if (filter.selected === undefined && !hasSavedKey) return false;
    if (Array.isArray(filter.selected) && filter.selected.length === 0 && !hasSavedKey) return false;
    return true;
  }) || [])
    // Sort view filters to appear at the top
    .sort((a, b) => {
      const aIsView = isViewFilter(a);
      const bIsView = isViewFilter(b);
      if (aIsView && !bIsView) return -1;
      if (!aIsView && bIsView) return 1;
      return 0;
    });

  if (!validFilters.length) {
    return (
      <Box py="sm" sx={{
        height: SAVED_FILTERS_HEIGHT,
        minHeight: SAVED_FILTERS_HEIGHT,
        maxHeight: SAVED_FILTERS_HEIGHT,
        borderBottom: '1px solid #e9ecef',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Text size="sm" c="#6b7280">
          No filters applied
        </Text>
      </Box>
    );
  }

  return (
    <Box
      py="xs"
      px={5}
      sx={{
        height: SAVED_FILTERS_HEIGHT,
        minHeight: SAVED_FILTERS_HEIGHT,
        maxHeight: SAVED_FILTERS_HEIGHT,
        overflow: 'hidden',
        borderBottom: '1px solid #e9ecef',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ScrollArea type="auto" scrollbarSize={6} sx={{ flex: 1, minHeight: 0 }}>
        {validFilters.map((filter) => {
          const values = formatFilterValues(filter, filtersConfig, currentUserId);
          const typeLabel = getFilterTypeLabel(filter);
          // Use special label for change log filters
          const filterLabel = isChangeLogFilter(filter) 
            ? formatChangeLogFilterLabel(filter)
            : (capitalizeFirstLetter(filter.label) || filter.name);
          const clearRowControl = (
            <Box
              mr={7}
              onClick={() => onRemoveFilter(filter.name)}
              title="Clear this filter"
              aria-label="Clear this filter"
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                marginLeft: 'auto',
                color: '#6b7280',
                '&:hover': {
                  color: MAIN_DARK_PRIMARY,
                },
              }}
            >
              <Text className="clear-text" fw={500} size="xs" sx={{ color: '#6b7280' }}>
                Clear
              </Text>
            </Box>
          );

          return (
            <Flex
              key={filter.name}
              justify="flex-start"
              align="center"
              pt={2}
              pb={14}
              px={19}
              gap="sm"
            >

              {/*   filter label */}

              <UnstyledButton
                onClick={() => onSelectFilter?.(filter)}
                title={onSelectFilter ? 'Focus this filter in the panel' : undefined}
                sx={{ ...pillSx, cursor: onSelectFilter ? 'pointer' : 'default', textAlign: 'left' }}
              >
                <Text size="xs" truncate fw={600}>
                  {filterLabel}
                </Text>
              </UnstyledButton>

              {/* operator */}
              <Box sx={{ ...pillSx, width: 'auto' }}>
                <Text size="xs">{typeLabel}</Text>
              </Box>

              {/* selected values */}
              <SelectedFilterValues values={values} />
                
              {/* View icon - inline next to selected values */}
              {isViewFilter(filter) && (
                <Tooltip label='Applied by view'>
                  <Box sx={{ flexShrink: 0 }}>
                    <StackFront color="#9ca3af" />
                  </Box>
                </Tooltip>
              )}

              {clearRowControl}
            </Flex>
          );
        })}
      </ScrollArea>
    </Box>
  );
};

export default SavedFilters;
